import AutoTopupCheckoutOptIn from '@/components/Credits/AutoTopupCheckoutOptIn';
import { autoTopupSettingsForPack, canOfferAutoTopupForPack } from '@/lib/autoTopupOffer';
import { useCreditPacksStore } from '@/store/creditPacks';
import { useToastStore } from '@/store/toast';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, View } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store/auth';
import { useModalStore } from '@/store/modal';
import { API_HOST } from '@/constants/config';
import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';
import useIosIap, { getCreditPackIapProductId } from '@/hooks/useIosIap';
import { nativeCheckoutResult, nativeSubscriptionPlans } from '@/lib/nativePayment';
import { resumeBtcPayCheckouts } from '@/lib/btcpayCheckout';
import { usersApi, whopApi, creditPacksApi } from '@/lib/api';
import { accountApi } from '@/lib/api/account';
import {
  CHECKOUT_ABANDON_REASON,
  createCheckoutAttemptTracker,
  nativeIapOutcome,
} from '@/lib/checkoutTracking';
import { trackCheckoutFunnelEvent, trackPurchaseCompleted } from '@/constants/utils/analyticsEvents';
import { checkValmarAvailable, clearValmarAvailabilityCache } from '@/constants/utils/valmarAvailability';
import {
  describeWhopPaymentError,
  isValmarBlockedError,
  readPreferInternationalCheckout,
  resolveIsInternationalFromServer,
  resolveWebCheckoutProvider,
  shouldPrefetchValmarAvailability,
  writePreferInternationalCheckout,
} from '@/constants/utils/webCheckoutRouting';
import { hasValidPlusAccess, shouldForceWhopCheckout, PROCESSOR } from '@/lib/purchaseUtils';
import { trackWhopPurchase } from '@/constants/utils/analytics';

const GabPurchaseContext = createContext(null);
const HELD_CHECKOUT_MESSAGE = 'Paid checkout is unavailable while this account is under review.';

function assertClientCanStartPaidCheckout(heldForReview) {
  if (!heldForReview) return;
  try {
    useModalStore.getState().actions.openModal('AccountReviewHoldModal', { source: 'checkout' });
  } catch (_) { /* best effort */ }
  const error = new Error(HELD_CHECKOUT_MESSAGE);
  error.code = 'paid_checkout_blocked';
  error.reason = 'held_for_review';
  throw error;
}

/**
 * Platform-specific purchase handling:
 * - Web: Uses Valmar (US) or Whop (international / known Whop subscriber / Valmar blocked)
 * - iOS: Uses Apple In-App Purchase (IAP) exclusively for all subscriptions and credit packs.
 *   No external payment (Valmar/Whop) fallback — required by App Store guidelines.
 * - Android: Uses Google Play In-App Purchase exclusively for all subscriptions and credit packs.
 *   No external payment (Valmar/Whop) fallback — mirrors the iOS IAP-only policy.
 */
export const isNativePlatform = Platform.OS === 'ios' || Platform.OS === 'android' || Platform.OS === 'macos';

// Which processor a native store purchase runs through, for the checkout funnel.
function nativeStoreProcessor() {
  return Platform.OS === 'android' ? 'google' : 'apple';
}



export function GabPurchaseProvider({ children }) {
  const { theme, colors } = useTheme();
  const userId = useAuthStore(state => state.user?.id);
  const userEmail = useAuthStore(state => state.user?.email);
  const userIsPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const userIsComped = useAuthStore(state => state.user?.flags?.isComped);
  const userHeldForReview = useAuthStore(state => state.user?.flags?.heldForReview === true);
  const plusExpiresAt = useAuthStore(state => state.user?.plusExpiresAt);
  const activePurchase = useAuthStore(state => state.user?.activePurchase);
  const hasWhopSubscription = useAuthStore(state => state.user?.whop?.hasSubscription);
  const isWhopSubscriber = shouldForceWhopCheckout(activePurchase, Boolean(hasWhopSubscription));
  const userHasValidPlus = hasValidPlusAccess({
    isPlus: userIsPlus,
    isComped: userIsComped,
    plusExpiresAt,
    purchase: activePurchase,
  });
  // This provider is mounted at the app root, so its effects run on every public
  // marketing page too. Anonymous visitors cannot reach checkout from any of them —
  // PricingPage, the pricing/credits modals and settings all route to /signup first —
  // so the two preflights below are pure speculation for them. Derived the same way
  // PricingPage derives it, since that is the gate that actually governs the access.
  const authToken = useAuthStore(state => state.token);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);
  const isAuthenticated = Boolean(authToken) && !isTokenExpired();
  useEffect(() => {
    if (!isAuthenticated || !userId || Platform.OS !== 'web') return;
    return resumeBtcPayCheckouts(userId);
  }, [isAuthenticated, userId]);
  const fetchUser = useAuthStore(state => state.fetchUser);
  const fetchUserCredits = useAuthStore(state => state.fetchUserCredits);
  const { isXS } = useResponsiveness();
  const iap = useIosIap();
  const [isOpen, setIsOpen] = useState(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const pendingPromiseRef = useRef({ resolve: null, reject: null, onEvent: null });
  const isDarkTheme = theme === 'muted' || theme === 'sepia' || theme === 'night' || theme === 'smoke' || theme === 'black';

  // Whop checkout state (international, known Whop subscriber, or Valmar blocked)
  const [whopCheckout, setWhopCheckout] = useState(null); // { planId, sessionId, type, fallbackFromValmar? }
  // Declines / failed 3D Secure leave Whop's own submit button spent, so we
  // surface the reason ourselves and offer a remount instead of a dead form.
  const [whopPaymentError, setWhopPaymentError] = useState(null);
  const [whopAutoOffer, setWhopAutoOffer] = useState(null);
  const [whopAutoChecked, setWhopAutoChecked] = useState(false);
  const whopAutoAuthorization = useRef(null);
  const whopContainerRef = useRef(null);
  const checkoutIframeRef = useRef(null);
  const pendingValmarCheckoutRef = useRef(null);
  const pendingValmarUpdateRef = useRef(null);
  const openWhopPlusCheckoutRef = useRef(null);
  const openWhopCreditsCheckoutRef = useRef(null);
  // One live checkout attempt at a time; every start resolves to exactly one of
  // completed / failed / abandoned so the funnel adds up (R-aug-02).
  const checkoutTrackerRef = useRef(null);
  if (!checkoutTrackerRef.current) {
    // One emit, two destinations: the backend keeps the per-user funnel history
    // for cohort work, Matomo puts the same step next to the campaign, landing
    // page and device that produced it. Sharing the emit is what keeps the two
    // from drifting into disagreeing funnels.
    checkoutTrackerRef.current = createCheckoutAttemptTracker((payload) => {
      usersApi.trackCheckoutEvent(payload);
      trackCheckoutFunnelEvent(payload);
    });
  }
  const checkoutTracker = checkoutTrackerRef.current;

  // Purchase completion analytics require confirmed fulfillment, not just a store charge.
  const trackNativeIapOutcome = useCallback((result) => {
    // `complete()` clears the attempt, so the context Matomo needs has to be
    // read off it first.
    const attempt = checkoutTracker.peek();
    switch (nativeIapOutcome(result)) {
      case 'completed':
        checkoutTracker.complete();
        trackPurchaseCompleted({
          checkoutType: attempt?.checkoutType,
          processor: attempt?.processor,
        });
        break;
      case 'pending':
        break;
      case 'abandoned':
        checkoutTracker.abandon(CHECKOUT_ABANDON_REASON.CLOSED);
        break;
      default:
        checkoutTracker.fail(result?.error);
    }
  }, [checkoutTracker]);

  // Check if user is international (non-US) - they use Whop checkout
  // Uses IP geolocation for accurate detection at checkout time
  const [isInternational, setIsInternational] = useState(false);
  const [ipCheckDone, setIpCheckDone] = useState(false);
  const checkoutSource = Platform.OS === 'web' ? 'web' : Platform.OS;

  // Where a bank's 3D Secure challenge returns to. Whop appends
  // ?status=success|error, and /payment-successful renders both outcomes.
  const whopReturnUrl = useMemo(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
    const checkoutType = whopCheckout?.purpose === 'update-payment'
      ? 'update-payment'
      : whopCheckout?.type === 'credits'
        ? 'credits'
        : 'plus';
    return `${window.location.origin}/payment-successful?checkoutType=${encodeURIComponent(checkoutType)}`;
  }, [whopCheckout?.purpose, whopCheckout?.type]);

  // Load Whop embed script when needed - must reload each time for new checkouts
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    if (!whopCheckout) return;
    
    // Small delay to ensure the DOM element is rendered
    const timer = setTimeout(() => {
      // Clear any previously mounted embed (e.g. after a new session is minted)
      // — React doesn't track the iframe Whop injected here.
      if (whopContainerRef.current) {
        whopContainerRef.current.innerHTML = '';
      }

      // Remove any existing Whop scripts to force re-initialization
      const existingScripts = document.querySelectorAll('script[src*="js.whop.com"]');
      existingScripts.forEach(s => s.remove());

      // Add fresh script
      const script = document.createElement('script');
      script.src = 'https://js.whop.com/static/checkout/loader.js';
      script.async = true;
      document.head.appendChild(script);
      console.log('[GabPurchase] Whop script added for:', whopCheckout.type);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [whopCheckout]);

  // Country resolved by our own backend from the request (Cloudflare edge, then
  // a local IP database). Free, unmetered and more trustworthy than the
  // third-party lookup, which has a small daily quota.
  const serverCountryCode = useAuthStore(state => state.user?.countryCode);
  const serverIsInternational = useAuthStore(state => state.user?.isInternational);
  const serverResolvedInternational = resolveIsInternationalFromServer({
    countryCode: serverCountryCode,
    isInternational: serverIsInternational,
  });

  // Resolve geo as soon as possible, preferring our own server's answer and
  // only paying for the third-party lookup when it told us nothing (anonymous
  // visitors, or before the user payload lands).
  useEffect(() => {
    // Nothing consumes `isInternational` while logged out (every reader is a settings
    // or subscription surface), and `ensureIpCheck()` resolves it on demand at checkout
    // anyway, so resolving it eagerly for an anonymous visitor buys nothing.
    if (!isAuthenticated) return undefined;

    if (serverResolvedInternational !== null) {
      setIsInternational(serverResolvedInternational);
      setIpCheckDone(true);
      return undefined;
    }

    if (ipCheckDone) return undefined;

    let cancelled = false;
    whopApi.checkIsInternational()
      .then((result) => {
        if (cancelled) return;
        console.log('[GabPurchase] IP geolocation result:', result);
        setIsInternational(result);
      })
      .catch((error) => {
        if (cancelled) return;
        // Whop clears cards worldwide; Valmar only clears US cards.
        console.warn('[GabPurchase] IP check failed, assuming international:', error);
        setIsInternational(true);
      })
      .finally(() => {
        if (!cancelled) setIpCheckDone(true);
      });

    return () => { cancelled = true; };
  }, [isAuthenticated, serverResolvedInternational, ipCheckDone]);

  // Prefetch Valmar availability for signed-in US web users so checkout routing is
  // ready sooner. See shouldPrefetchValmarAvailability for why the auth gate is load
  // bearing — the probe opens live payment iframes, and this provider is mounted at the
  // app root, so it would otherwise run on every marketing page.
  useEffect(() => {
    if (!shouldPrefetchValmarAvailability({
      platform: Platform.OS,
      isAuthenticated,
      ipCheckDone,
      isInternational,
      isWhopSubscriber,
    })) return;

    checkValmarAvailable().catch((error) => {
      console.warn('[GabPurchase] Valmar availability preflight failed:', error);
    });
  }, [isAuthenticated, ipCheckDone, isInternational, isWhopSubscriber]);

  // Helper to ensure geo is resolved before checkout
  const ensureIpCheck = useCallback(async () => {
    if (serverResolvedInternational !== null) return serverResolvedInternational;
    if (ipCheckDone) return isInternational;
    
    try {
      const result = await whopApi.checkIsInternational();
      console.log('[GabPurchase] IP geolocation result (on-demand):', result);
      setIsInternational(result);
      setIpCheckDone(true);
      return result;
    } catch (error) {
      // Sending a foreign buyer to Valmar guarantees failure, so an unresolved
      // country routes to Whop instead.
      console.warn('[GabPurchase] IP check failed, assuming international:', error);
      setIsInternational(true);
      setIpCheckDone(true);
      return true;
    }
  }, [ipCheckDone, isInternational, serverResolvedInternational]);

  const resolveCheckoutProvider = useCallback(async () => {
    const userIsInternational = await ensureIpCheck();
    const preferInternational = readPreferInternationalCheckout();
    const skipValmarProbe = userIsInternational || isWhopSubscriber || preferInternational;
    const valmarAvailable = skipValmarProbe ? true : await checkValmarAvailable();
    const provider = resolveWebCheckoutProvider({
      isInternational: userIsInternational,
      valmarAvailable,
      isWhopSubscriber,
      preferInternational,
    });
    console.log('[GabPurchase] Checkout provider resolved:', {
      isInternational: userIsInternational,
      isWhopSubscriber,
      preferInternational,
      valmarAvailable,
      provider,
    });
    return { provider, userIsInternational, valmarAvailable };
  }, [ensureIpCheck, isWhopSubscriber]);

  const beginPendingCheckoutPromise = useCallback((options = {}) => new Promise((resolve, reject) => {
    pendingPromiseRef.current = {
      resolve,
      reject,
      onEvent: typeof options.onEvent === 'function' ? options.onEvent : null,
    };
  }), []);

  const launchWhopCheckoutUi = useCallback((response, options = {}, { fallbackFromValmar = false, type = 'plus', purpose = null } = {}) => {
    if (fallbackFromValmar) {
      writePreferInternationalCheckout();
    }
    pendingValmarCheckoutRef.current = null;
    pendingValmarUpdateRef.current = null;
    setCheckoutUrl(null);
    setWhopPaymentError(null);
    const promoHint = typeof options.promoCode === 'string'
      ? options.promoCode.trim().toUpperCase()
      : '';
    const offerUser = useAuthStore.getState().user;
    const offerPack = useCreditPacksStore.getState().creditPacks.find(pack => pack.id === options.creditPackId);
    setWhopAutoChecked(false); whopAutoAuthorization.current = null;
    setWhopAutoOffer(type === 'credits' && !options.autoTopupHandled && canOfferAutoTopupForPack({ user: offerUser, usesWhopCheckout: true, platform: Platform.OS, pack: offerPack })
      ? { pack: offerPack, settings: autoTopupSettingsForPack(offerPack, { version: offerUser.autoTopup.version, provider: 'whop', paymentMethodFingerprint: offerUser.autoTopup.paymentMethodFingerprint }) } : null);
    setWhopCheckout({
      planId: response.planId,
      sessionId: response.sessionId,
      // Whop's hosted checkout for the same session. Kept as an escape hatch:
      // a bank challenge that cannot finish inside the embed usually completes
      // at the top level.
      hostedCheckoutUrl: response.checkoutUrl || null,
      type,
      creditPackId: type === 'credits' ? options.creditPackId : undefined,
      fallbackFromValmar,
      purpose,
      promoHint: purpose === 'update-payment' ? '' : promoHint,
      options,
    });
    setIsOpen(true);
    // Updating a card is not a purchase funnel entry.
    if (purpose !== 'update-payment') {
      checkoutTracker.start({ processor: 'whop', checkoutType: type === 'credits' ? 'credits' : 'plus' });
    }
  }, [checkoutTracker]);

  const openWhopPlusCheckout = useCallback(async (options = {}, { fallbackFromValmar = false, keepPendingPromise = false, purpose = null } = {}) => {
    assertClientCanStartPaidCheckout(userHeldForReview);
    const rawPlan = options.plusPlanId || 'monthly';
    const planLength = (
      rawPlan === 'yearly' ||
      rawPlan === 'monthly' ||
      rawPlan === 'premium-yearly' ||
      rawPlan === 'premium-monthly'
    ) ? rawPlan : 'monthly';
    // Any web checkout by an existing Plus subscriber is an extension or tier
    // change. Preserve their remaining paid time even when the caller came
    // from a generic pricing CTA that did not explicitly set mode=extend.
    const isExtend = options.mode === 'extend' || userHasValidPlus;
    console.log('[GabPurchase] Opening Whop embedded checkout for Plus, plan:', planLength, 'isExtend:', isExtend, 'fallbackFromValmar:', fallbackFromValmar);

    const response = await whopApi.createPlusCheckout({
      planLength,
      source: checkoutSource,
      isExtend,
      allowPromoCodes: purpose !== 'update-payment',
      checkoutPurpose: purpose === 'update-payment' ? 'update-payment' : 'plus',
    });
    console.log('[GabPurchase] Whop API response:', response);

    if (!response?.planId) {
      throw new Error('No plan ID returned from Whop API');
    }

    launchWhopCheckoutUi(
      response,
      { ...options, plusPlanId: planLength, isExtend },
      { fallbackFromValmar, type: 'plus', purpose }
    );
    return keepPendingPromise ? undefined : beginPendingCheckoutPromise(options);
  }, [beginPendingCheckoutPromise, checkoutSource, launchWhopCheckoutUi, userHasValidPlus, userHeldForReview]);

  const openWhopCreditsCheckout = useCallback(async (options = {}, { fallbackFromValmar = false, keepPendingPromise = false } = {}) => {
    assertClientCanStartPaidCheckout(userHeldForReview);
    console.log('[GabPurchase] Opening Whop embedded credits checkout', 'fallbackFromValmar:', fallbackFromValmar);

    const response = await whopApi.createCreditsCheckout({
      creditPackId: options.creditPackId,
      source: checkoutSource,
    });
    console.log('[GabPurchase] Whop API response:', response);

    if (!response?.planId) {
      throw new Error('No plan ID returned from Whop API');
    }

    launchWhopCheckoutUi(response, options, { fallbackFromValmar, type: 'credits' });
    return keepPendingPromise ? undefined : beginPendingCheckoutPromise(options);
  }, [beginPendingCheckoutPromise, checkoutSource, launchWhopCheckoutUi, userHeldForReview]);

  useEffect(() => {
    openWhopPlusCheckoutRef.current = openWhopPlusCheckout;
    openWhopCreditsCheckoutRef.current = openWhopCreditsCheckout;
  }, [openWhopPlusCheckout, openWhopCreditsCheckout]);

  // Handle Whop checkout completion
  const handleWhopComplete = useCallback(async (planId, receiptId) => {
    console.log('[GabPurchase] Whop checkout complete:', { planId, receiptId });
    const attempt = checkoutTracker.peek();
    checkoutTracker.complete();
    trackPurchaseCompleted({
      checkoutType: attempt?.checkoutType,
      processor: attempt?.processor || 'whop',
    });

    // Verify fulfillment before claiming success. The embed's complete event
    // only proves Whop accepted the charge — the backend may still withhold
    // entitlement (review hold) or the fulfillment webhook may lag. Snapshot
    // the pre-purchase state BEFORE any refresh so we can detect the delta.
    const checkoutType = attempt?.checkoutType === 'credits' ? 'credits' : 'plus';
    const beforeUser = useAuthStore.getState().user || {};
    const beforeCredits = Number(beforeUser?.credits?.totalAvailable) || 0;
    const beforePlusExpiry = beforeUser?.plusExpiresAt ? new Date(beforeUser.plusExpiresAt).getTime() : 0;

    // Close the checkout modal
    setWhopCheckout(null);
    setIsOpen(false);

    // Resolve any pending promise
    const resolve = pendingPromiseRef.current.resolve;
    pendingPromiseRef.current = { resolve: null, reject: null, onEvent: null };
    const autoAuthorization = whopAutoAuthorization.current;
    whopAutoAuthorization.current = null;
    let autoNotice = '';
    if (autoAuthorization) {
      try {
        const response = await creditPacksApi.updateAutoTopupSettings({ ...autoAuthorization, afterPurchase: true, afterPurchasePaymentId: receiptId });
        if (!response.autoTopup) throw new Error('Settings not confirmed');
        useAuthStore.getState().updateAutoTopupSettings(response.autoTopup);
        autoNotice = ' Auto top-up is enabled.';
      } catch (_) { autoNotice = ' Auto top-up could not be enabled. Review it in Credits settings.'; }
    }
    trackWhopPurchase({ id: receiptId || planId });

    const refreshUser = async () => {
      const refreshPromises = [];
      if (fetchUser) refreshPromises.push(fetchUser());
      if (fetchUserCredits) refreshPromises.push(fetchUserCredits({ force: true }));
      await Promise.allSettled(refreshPromises);
    };
    await refreshUser();

    // 'true' = fulfilled, 'held' = review hold, false = not (yet) visible.
    const checkFulfillment = () => {
      const user = useAuthStore.getState().user;
      if (!user) return false;
      if (user.flags?.heldForReview === true) return 'held';
      if (checkoutType === 'credits') {
        return (Number(user?.credits?.totalAvailable) || 0) > beforeCredits;
      }
      if (user.flags?.isPlus !== true) return false;
      const expiry = user.plusExpiresAt ? new Date(user.plusExpiresAt).getTime() : 0;
      return expiry > beforePlusExpiry;
    };
    let outcome = checkFulfillment();
    const FULFILLMENT_WAIT_MS = 15000;
    const deadline = Date.now() + FULFILLMENT_WAIT_MS;
    while (outcome === false && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2500));
      await refreshUser();
      outcome = checkFulfillment();
    }
    const pendingMessage = 'Payment received. Activation is still processing — if it does not appear shortly, contact support.';
    if (resolve) {
      resolve({ success: outcome === true, planId, receiptId, error: outcome === true ? undefined : pendingMessage });
    }
    if (outcome === true) {
      useToastStore.getState().actions.addToast(`Payment completed.${autoNotice}`, 'success');
    } else if (outcome === 'held') {
      try {
        useModalStore.getState().actions.openModal('AccountReviewHoldModal', { source: 'checkout_complete' });
      } catch (_) { /* best effort */ }
      useToastStore.getState().actions.addToast('Payment received. Your account is under review — please contact support.', 'error');
    } else {
      useToastStore.getState().actions.addToast(pendingMessage, 'error');
    }
  }, [checkoutTracker, fetchUser, fetchUserCredits]);

  // Whop fires this for declines and for failed 3D Secure / external-redirect
  // payments. Without it the embed just disables its own submit button, which
  // reads as a frozen checkout the buyer can never complete.
  const handleWhopPaymentError = useCallback((error) => {
    const message = describeWhopPaymentError(error);
    console.warn('[GabPurchase] Whop payment error:', message, error);
    setWhopPaymentError(message);
    setWhopAutoChecked(false); whopAutoAuthorization.current = null;
    checkoutTracker.fail(message);

    // A failed attempt may have tripped a payment-failure review hold on the
    // backend (3 declines / 24h). The hold lands asynchronously via webhook,
    // so refetch now — otherwise the stale client flag lets the user retry
    // into a checkout whose charge would be silently withheld.
    try {
      useAuthStore.getState().fetchUserSlim?.().then?.(() => {
        if (useAuthStore.getState().user?.flags?.heldForReview === true) {
          useModalStore.getState().actions.openModal('AccountReviewHoldModal', { source: 'payment_error' });
        }
      }).catch?.(() => {});
    } catch (_) { /* best effort */ }

    if (typeof pendingPromiseRef.current.onEvent === 'function') {
      try {
        pendingPromiseRef.current.onEvent({ name: 'whopPaymentError', data: { message } });
      } catch (_) {}
    }
  }, [checkoutTracker]);

  // Whop treats a session that already failed as spent, so retrying means
  // minting a new one and remounting the embed.
  const retryWhopCheckout = useCallback(async () => {
    const current = whopCheckout;
    if (!current) return;

    setWhopPaymentError(null);

    try {
      if (current.type === 'credits') {
        await openWhopCreditsCheckoutRef.current?.(current.options || {}, {
          fallbackFromValmar: current.fallbackFromValmar,
          keepPendingPromise: true,
        });
      } else {
        await openWhopPlusCheckoutRef.current?.(current.options || {}, {
          fallbackFromValmar: current.fallbackFromValmar,
          keepPendingPromise: true,
          purpose: current.purpose,
        });
      }
    } catch (error) {
      console.error('[GabPurchase] Failed to restart Whop checkout:', error);
      setWhopPaymentError(describeWhopPaymentError(error));
    }
  }, [whopCheckout]);

  // Set up global callbacks for Whop checkout
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    
    // Create global callback function for Whop's onComplete
    window.__gabWhopCheckoutComplete = handleWhopComplete;
    window.__gabWhopPaymentError = handleWhopPaymentError;
    
    return () => {
      delete window.__gabWhopCheckoutComplete;
      delete window.__gabWhopPaymentError;
    };
  }, [handleWhopComplete, handleWhopPaymentError]);

  const closeCheckout = useCallback((reason) => {
    setIsOpen(false);
    setIsIframeLoaded(false);
    setCheckoutUrl(null);
    setWhopCheckout(null);
    setWhopPaymentError(null);
    pendingValmarCheckoutRef.current = null;
    pendingValmarUpdateRef.current = null;
    // No-op unless a purchase attempt is actually open, so closing an
    // update-payment iframe is not counted as an abandoned checkout.
    checkoutTracker.abandon(CHECKOUT_ABANDON_REASON.CLOSED);
    if (pendingPromiseRef.current.reject) {
      pendingPromiseRef.current.reject(reason instanceof Error ? reason : new Error(reason || 'Checkout closed'));
    }
    pendingPromiseRef.current = { resolve: null, reject: null, onEvent: null };
  }, [checkoutTracker]);

  const closeCreditPacksCheckout = useCallback((reason) => {
    setIsOpen(false);
    setIsIframeLoaded(false);
    setCheckoutUrl(null);
    setWhopCheckout(null);
    setWhopPaymentError(null);
    pendingValmarCheckoutRef.current = null;
    pendingValmarUpdateRef.current = null;
    checkoutTracker.abandon(CHECKOUT_ABANDON_REASON.CLOSED);
    if (pendingPromiseRef.current.reject) {
      pendingPromiseRef.current.reject(reason instanceof Error ? reason : new Error(reason || 'Checkout closed'));
    }
    pendingPromiseRef.current = { resolve: null, reject: null, onEvent: null };
  }, [checkoutTracker]);

  const fallbackToWhopCheckout = useCallback(async (pendingCheckout) => {
    if (!pendingCheckout) return false;

    clearValmarAvailabilityCache();
    pendingValmarCheckoutRef.current = null;
    setCheckoutUrl(null);
    setIsIframeLoaded(false);

    try {
      if (pendingCheckout.type === 'plus') {
        await openWhopPlusCheckoutRef.current?.(pendingCheckout.options, {
          fallbackFromValmar: true,
          keepPendingPromise: true,
        });
      } else if (pendingCheckout.type === 'credits') {
        await openWhopCreditsCheckoutRef.current?.(pendingCheckout.options, {
          fallbackFromValmar: true,
          keepPendingPromise: true,
        });
      } else {
        return false;
      }
      return true;
    } catch (error) {
      console.error('[GabPurchase] Failed to fall back to Whop checkout:', error);
      const reject = pendingPromiseRef.current.reject;
      pendingPromiseRef.current = { resolve: null, reject: null, onEvent: null };
      if (reject) {
        reject(error instanceof Error ? error : new Error('Unable to open alternate checkout'));
      }
      setIsOpen(false);
      setWhopCheckout(null);
      return false;
    }
  }, []);


  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleMessage = (event) => {
      if (!checkoutIframeRef.current || !event.source || event.source !== checkoutIframeRef.current.contentWindow) {
        return;
      }
      if (checkoutUrl) {
        try {
          const expectedOrigin = new URL(checkoutUrl, window.location.href).origin;
          if (event.origin && event.origin !== expectedOrigin) return;
        } catch (_) {}
      }
      const data = event?.data;
      if (!data || (data.source !== 'checkout-iframe' && data.source !== 'credit-checkout-iframe' && data.source !== 'update-payment-iframe')) return;

      const action = data.action;
      const payload = data.data || {};

      if (typeof pendingPromiseRef.current.onEvent === 'function') {
        try { pendingPromiseRef.current.onEvent({ name: action, data: payload }); } catch (_) {}
      }

      switch (action) {
        case 'closeCheckoutIframe':
          closeCheckout('User closed checkout');
          break;
        case 'checkoutStarted':
        case 'creditCheckoutValidated':
        case 'checkoutValidated':
        case 'checkoutTokenizationStarted':
        case 'checkoutTokenizationSuccess':
        case 'updatePaymentStarted':
        case 'updatePaymentValidated':
        case 'updatePaymentTokenizationStarted':
        case 'updatePaymentTokenizationSuccess':
          break;
        case 'updatePaymentSuccess': {
          const resolve = pendingPromiseRef.current.resolve;
          // Close the checkout UI
          setIsOpen(false);
          setIsIframeLoaded(false);
          setCheckoutUrl(null);
          // Resolve any awaiting promise
          pendingPromiseRef.current = { resolve: null, reject: null, onEvent: null };
          if (resolve) resolve({ success: true, ...payload });
          // Inform user
          try { window.alert('Payment method updated successfully!'); } catch (_) {}
          try { window.location.reload(); } catch (_) {}
          break;
        }
        case 'updatePaymentCancelled':
        case 'updatePaymentClose': {
          closeCheckout('User closed update payment');
          break;
        }
        case 'creditCheckoutSuccess':
        case 'checkoutSuccess': {
          const attempt = checkoutTracker.peek();
          checkoutTracker.complete();
          trackPurchaseCompleted({
            checkoutType: attempt?.checkoutType || (action === 'creditCheckoutSuccess' ? 'credits' : 'plus'),
            processor: attempt?.processor || 'valmar',
          });
          const resolve = pendingPromiseRef.current.resolve;
          // Close the checkout UI
          setIsOpen(false);
          setIsIframeLoaded(false);
          setCheckoutUrl(null);
          // Resolve any awaiting promise
          pendingPromiseRef.current = { resolve: null, reject: null, onEvent: null };
          if (resolve) resolve({ success: true, ...payload });
          trackWhopPurchase({
            id: payload.purchaseId || payload.receiptId || payload.creditPackId,
          });
          // Inform user and refresh the page
          try { window.alert('Thank you! Your purchase was successful.'); } catch (_) {}
          try { window.location.reload(); } catch (_) {}
          break;
        }
        case 'checkoutError':
        case 'checkoutNetworkError':
        case 'creditCheckoutError':
        case 'creditCheckoutNetworkError': {
          // Declines and gateway errors. A no-op when nothing is open.
          checkoutTracker.fail(payload?.errorMessage);
          break;
        }
        case 'updatePaymentError':
        case 'updatePaymentNetworkError': {
          break;
        }
        case 'updatePaymentTokenizationError': {
          const pendingUpdate = pendingValmarUpdateRef.current;
          if (!pendingUpdate || !isValmarBlockedError(payload)) {
            break;
          }

          console.warn('[GabPurchase] Valmar payment update blocked in iframe, falling back to Whop:', payload?.errorMessage);
          clearValmarAvailabilityCache();
          pendingValmarUpdateRef.current = null;
          setCheckoutUrl(null);
          setIsIframeLoaded(false);

          const updateError = new Error('The card update form is unavailable. Allow the secure payment form or contact support. No new subscription was started.');
          const reject = pendingPromiseRef.current.reject;
          pendingPromiseRef.current = { resolve: null, reject: null, onEvent: null };
          reject?.(updateError);
          useToastStore.getState().actions.addToast(updateError.message, 'error');
          setIsOpen(false);
          setWhopCheckout(null);
          break;
        }
        case 'checkoutTokenizationError':
        case 'creditCheckoutTokenizationError': {
          const pendingCheckout = pendingValmarCheckoutRef.current;
          if (!pendingCheckout || !isValmarBlockedError(payload)) {
            // No fallback is coming, so the card itself was rejected at
            // tokenization and this attempt is over. Without this the buyer
            // closes the modal instead and the decline is filed as
            // abandonment.
            checkoutTracker.fail(payload?.errorMessage || 'tokenization_error');
            break;
          }

          console.warn('[GabPurchase] Valmar checkout blocked in iframe, falling back to Whop:', payload?.errorMessage);
          fallbackToWhopCheckout({
            ...pendingCheckout,
            options: {
              ...pendingCheckout.options,
              promoCode: payload?.promoCode || pendingCheckout.options?.promoCode,
            },
          });
          break;
        }
        default:
          break;
      }
    };

    if (Platform.OS !== 'web') return;
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [checkoutTracker, closeCheckout, fallbackToWhopCheckout, checkoutUrl]);

  // Leaving the page mid-payment is the one abandonment no UI callback can
  // report: nothing is closed, nothing fails, the attempt just stops existing.
  // `pagehide` covers tab close, reload and navigation, where `beforeunload` is
  // unreliable on mobile Safari. A no-op when no attempt is open.
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handlePageHide = (event) => {
      // `persisted` means the page is going into the back/forward cache and may
      // come back with the checkout still open, so it is not an abandonment.
      if (event?.persisted) return;
      checkoutTracker.abandon(CHECKOUT_ABANDON_REASON.NAVIGATED_AWAY);
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [checkoutTracker]);

  const buildCheckoutUrl = useCallback(({
    sandbox = false, // !!! TESTING !!!
    plusPlanId,
    creditPackId,
    promoCode,
    theme: themeOverride,
    mode,
  } = {}) => {
    const baseUrl = API_HOST;
    const path = creditPackId ? '/checkout/credit-payment' : '/checkout/payment';
    const endpoint = `${baseUrl}${path}`;
    const params = new URLSearchParams();
    
    if (userId) params.set('userId', String(userId));
    if (userEmail) params.set('email', String(userEmail));

    const effectiveTheme = themeOverride || (isDarkTheme ? 'dark' : 'light');
    if (effectiveTheme) params.set('theme', effectiveTheme);

    if (
      plusPlanId === 'monthly' ||
      plusPlanId === 'yearly' ||
      plusPlanId === 'premium-monthly' ||
      plusPlanId === 'premium-yearly'
    ) {
      params.set('planId', plusPlanId);
    }
    if (creditPackId) params.set('creditPackId', creditPackId);
    if (promoCode) params.set('promoCode', String(promoCode));
    if (!creditPackId && (mode === 'extend' || userHasValidPlus)) {
      params.set('mode', 'extend');
    }

    if (sandbox) params.set('sandbox', '1');

    return `${endpoint}?${params.toString()}`;
  }, [userEmail, userId, isDarkTheme, userHasValidPlus]);


  const openCheckout = useCallback(async (options = {}) => {
    if (!userId || !userEmail) {
      return Promise.reject(new Error('User must be logged in to purchase a subscription'));
    }
    assertClientCanStartPaidCheckout(userHeldForReview);

    // Plus Premium is web-only — never route through native IAP.
    const isPremiumPlan = typeof options.plusPlanId === 'string'
      && options.plusPlanId.startsWith('premium-');
    if (isPremiumPlan && Platform.OS !== 'web') {
      return Promise.reject(new Error('Plus Premium is only available on the web.'));
    }

    // iOS/Android: use IAP for subscription purchases when store is connected
    if (Platform.OS !== 'web' && options.plusPlanId) {
      const subscribedStore = activePurchase?.processor;
      const deviceStore = Platform.OS === 'android' ? PROCESSOR.GOOGLE : PROCESSOR.APPLE;
      if (activePurchase?.status === 'active' && [PROCESSOR.APPLE, PROCESSOR.GOOGLE].includes(subscribedStore)
        && subscribedStore !== deviceStore) {
        throw new Error(`Manage this subscription in ${subscribedStore === PROCESSOR.GOOGLE ? 'Google Play' : 'the App Store'} to avoid a second subscription.`);
      }
      let storeConnected = !!iap.isConnected;
      const productForCadence = nativeSubscriptionPlans(iap.products, Platform.OS)
        .find(plan => plan.cadence === options.plusPlanId);
      const subscriptionCandidates = [productForCadence?.productId
        || iap.PRODUCT_IDS?.[String(options.plusPlanId).toUpperCase()]].filter(Boolean);
      let iapProduct = null;
      let selectedProductId = null;

      for (const candidate of subscriptionCandidates) {
        const product = iap.getProduct(candidate);
        if (product) {
          iapProduct = product;
          selectedProductId = candidate;
          break;
        }
      }

      console.log('[GabPurchase] IAP check:', {
        productCandidates: subscriptionCandidates,
        selectedProductId,
        isConnected: storeConnected,
        hasProduct: !!iapProduct,
        totalProducts: iap.products?.length,
        totalCreditPacks: iap.creditPackProducts?.length,
      });

      // If connected but products not loaded yet, try fetching them now
      if (subscriptionCandidates.length > 0 && storeConnected && !iapProduct) {
        console.log('[GabPurchase] Products not loaded, retrying fetchProducts...');
        try {
          const fetched = await iap.fetchProducts();
          const fetchedSubscriptions = fetched?.subscriptions || [];
          for (const candidate of subscriptionCandidates) {
            const product = fetchedSubscriptions.find((p) => p?.productId === candidate);
            if (product) {
              iapProduct = product;
              selectedProductId = candidate;
              break;
            }
          }
          console.log('[GabPurchase] After refetch, hasProduct:', !!iapProduct, 'selectedProductId:', selectedProductId);
        } catch (fetchErr) {
          console.error('[GabPurchase] fetchProducts retry failed:', fetchErr);
        }
      }

      // If not connected, try connecting first
      if (subscriptionCandidates.length > 0 && !storeConnected) {
        console.log('[GabPurchase] Store not connected, attempting connect...');
        try {
          const connected = await iap.connect();
          storeConnected = !!connected;
          const fetched = await iap.fetchProducts();
          const fetchedSubscriptions = fetched?.subscriptions || [];
          for (const candidate of subscriptionCandidates) {
            const product = fetchedSubscriptions.find((p) => p?.productId === candidate);
            if (product) {
              iapProduct = product;
              selectedProductId = candidate;
              break;
            }
          }
          console.log('[GabPurchase] After connect+fetch, hasProduct:', !!iapProduct, 'selectedProductId:', selectedProductId);
        } catch (connectErr) {
          console.error('[GabPurchase] Connect retry failed:', connectErr);
        }
      }

      if (selectedProductId && storeConnected && iapProduct) {
        console.log('[GabPurchase] Using IAP for subscription purchase:', selectedProductId);
        checkoutTracker.start({ processor: nativeStoreProcessor(), checkoutType: 'plus' });
        if (!nativeSubscriptionPlans([iapProduct], Platform.OS).some(plan => plan.cadence === options.plusPlanId)) {
          throw new Error('This store product does not match the selected billing period. Please reload the plans.');
        }
        const result = await iap.purchaseProduct(selectedProductId, iapProduct);
        trackNativeIapOutcome(result);
        return nativeCheckoutResult(result);
      }
      // iOS/Android/macOS: IAP is required — no external payment fallback
      if (isNativePlatform) {
        const storeName = (Platform.OS === 'ios' || Platform.OS === 'macos') ? 'App Store' : 'Google Play';
        const reason = !storeConnected
          ? `${storeName} connection failed`
          : !iapProduct
            ? `Products [${subscriptionCandidates.join(', ')}] not found in ${storeName} (${iap.products?.length || 0} loaded)`
            : 'Unknown error';
        console.error(`[GabPurchase] IAP not available on ${Platform.OS}:`, reason);
        throw new Error(`In-App Purchase is not available: ${reason}. Please restart the app and try again.`);
      }
      console.log('[GabPurchase] IAP not available for subscription, falling back to web checkout');
    }

    // Native (iOS/Android/macOS): block any non-IAP purchase path
    if (isNativePlatform) {
      console.error(`[GabPurchase] ${Platform.OS} requires IAP for all purchases`);
      throw new Error('In-App Purchase is not available right now. Please restart the app and try again.');
    }

    if (options.forceWhop) {
      clearValmarAvailabilityCache();
      pendingValmarCheckoutRef.current = { type: 'plus', options };
      try {
        await openWhopPlusCheckout(options, {
          fallbackFromValmar: true,
          keepPendingPromise: true,
        });
        return beginPendingCheckoutPromise(options);
      } catch (error) {
        console.error('[GabPurchase] Forced Whop checkout error:', error);
        throw error;
      }
    }

    // Check if user is international or Valmar is blocked (e.g. ad/script blockers)
    const { provider, userIsInternational, valmarAvailable } = await resolveCheckoutProvider();

    if (provider === 'whop') {
      try {
        return await openWhopPlusCheckout(options, {
          fallbackFromValmar: !userIsInternational && !valmarAvailable,
        });
      } catch (error) {
        console.error('[GabPurchase] Whop checkout error:', error);
        throw error;
      }
    }

    // US users with Valmar available continue with Valmar checkout
    pendingValmarCheckoutRef.current = { type: 'plus', options };
    const url = await accountApi.authorizeCheckoutUrl(
      buildCheckoutUrl(options), options.creditPackId ? 'credits' : 'plus',
    );
    setWhopCheckout(null);
    setCheckoutUrl(url);
    setIsIframeLoaded(false);
    setIsOpen(true);
    checkoutTracker.start({ processor: 'valmar', checkoutType: 'plus' });

    return beginPendingCheckoutPromise(options);
  }, [buildCheckoutUrl, beginPendingCheckoutPromise, checkoutTracker, trackNativeIapOutcome, openWhopPlusCheckout, resolveCheckoutProvider, iap, userHeldForReview, activePurchase, userId, userEmail]);

  const openCreditPacksCheckout = useCallback(async (options = {}) => {
    if (!userId || !userEmail) {
      return Promise.reject(new Error('User must be logged in to purchase credits'));
    }
    assertClientCanStartPaidCheckout(userHeldForReview);

    // Native (iOS/Android): use IAP for credit packs when product is available
    if (Platform.OS !== 'web') {
      let storeConnected = !!iap.isConnected;
      // Resolve credits from options — may be undefined if caller only passed creditPackId
      let credits = options.credits;
      if (credits == null && options.creditPackId) {
        // Try to extract credits from creditPackId (e.g. "credit-pack-700" -> 700)
        const match = String(options.creditPackId).match(/(\d+)/);
        if (match) credits = Number(match[1]);
        console.log('[GabPurchase] Resolved credits from creditPackId:', options.creditPackId, '->', credits);
      }

      if (credits != null) {
        const iapProductId = getCreditPackIapProductId(credits);
        let hasProduct = iapProductId ? !!iap.getProduct(iapProductId) : false;

        console.log('[GabPurchase] Credit pack IAP check:', {
          credits,
          iapProductId,
          isConnected: storeConnected,
          hasProduct,
          totalCreditPacks: iap.creditPackProducts?.length,
        });

        // If connected but product not loaded, retry fetch
        if (iapProductId && storeConnected && !hasProduct) {
          console.log('[GabPurchase] Credit pack product not loaded, retrying fetchProducts...');
          try {
            const fetched = await iap.fetchProducts();
            const fetchedCreditPacks = fetched?.creditPacks || [];
            hasProduct = fetchedCreditPacks.some((p) => p?.productId === iapProductId);
            console.log('[GabPurchase] After refetch, hasProduct:', hasProduct);
          } catch (fetchErr) {
            console.error('[GabPurchase] fetchProducts retry failed:', fetchErr);
          }
        }

        // If not connected, try connecting first
        if (iapProductId && !storeConnected) {
          console.log('[GabPurchase] Store not connected for credit pack, attempting connect...');
          try {
            const connected = await iap.connect();
            storeConnected = !!connected;
            const fetched = await iap.fetchProducts();
            const fetchedCreditPacks = fetched?.creditPacks || [];
            hasProduct = fetchedCreditPacks.some((p) => p?.productId === iapProductId);
          } catch (connectErr) {
            console.error('[GabPurchase] Connect retry failed:', connectErr);
          }
        }

        if (iapProductId && storeConnected && hasProduct) {
          checkoutTracker.start({ processor: nativeStoreProcessor(), checkoutType: 'credits' });
          const result = await iap.purchaseCreditPack(credits);
          trackNativeIapOutcome(result);
          const outcome = nativeCheckoutResult(result);
          if (outcome.pending) useToastStore.getState().actions.addToast('Your purchase is pending verification. Use Restore; do not purchase again.', 'info');
          else if (!outcome.success && !outcome.cancelled) useToastStore.getState().actions.addToast(outcome.error || 'Purchase failed.', 'error');
          return outcome;
        }

        // iOS/Android/macOS: IAP is required — no external payment fallback
        if (isNativePlatform) {
          const storeName = (Platform.OS === 'ios' || Platform.OS === 'macos') ? 'App Store' : 'Google Play';
          const reason = !storeConnected
            ? `${storeName} connection failed`
            : !iapProductId
              ? `No IAP product for ${credits} credits`
              : `Product "${iapProductId}" not found (${iap.creditPackProducts?.length || 0} loaded)`
            ;
          console.error(`[GabPurchase] IAP not available for credit pack on ${Platform.OS}:`, reason);
          throw new Error(`Credit pack purchase not available: ${reason}. Please restart the app and try again.`);
        }
      } else if (isNativePlatform) {
        console.error(`[GabPurchase] Cannot determine credits amount for IAP on ${Platform.OS}`);
        throw new Error('Credit pack purchase failed: unable to determine credit amount. Please try again.');
      }
    }

    // Native (iOS/Android/macOS): block any non-IAP credit pack purchase path
    if (isNativePlatform) {
      console.error(`[GabPurchase] ${Platform.OS} requires IAP for all credit pack purchases`);
      throw new Error('In-App Purchase is not available right now. Please restart the app and try again.');
    }

    // Check if user is international or Valmar is blocked (e.g. ad/script blockers)
    const { provider, userIsInternational, valmarAvailable } = await resolveCheckoutProvider();

    if (provider === 'whop') {
      try {
        return await openWhopCreditsCheckout(options, {
          fallbackFromValmar: !userIsInternational && !valmarAvailable,
        });
      } catch (error) {
        console.error('[GabPurchase] Whop checkout error:', error);
        throw error;
      }
    }

    // US users with Valmar available continue with Valmar checkout
    pendingValmarCheckoutRef.current = { type: 'credits', options };
    const url = await accountApi.authorizeCheckoutUrl(
      buildCheckoutUrl(options), options.creditPackId ? 'credits' : 'plus',
    );
    setWhopCheckout(null);
    setCheckoutUrl(url);
    setIsIframeLoaded(false);
    setIsOpen(true);
    checkoutTracker.start({ processor: 'valmar', checkoutType: 'credits' });

    return beginPendingCheckoutPromise(options);
  }, [buildCheckoutUrl, beginPendingCheckoutPromise, checkoutTracker, trackNativeIapOutcome, openWhopCreditsCheckout, resolveCheckoutProvider, iap, userHeldForReview]);

  const openUpdatePaymentMethod = useCallback(async (updateUrl, options = {}) => {
    if (Platform.OS !== 'web') {
      return Promise.reject(new Error('Payment method update is only available on web'));
    }
    assertClientCanStartPaidCheckout(userHeldForReview);

    if (!userId || !userEmail) {
      return Promise.reject(new Error('User must be logged in to update payment method'));
    }

    if (!updateUrl) {
      return Promise.reject(new Error('Update URL is required'));
    }

    const supportedPlanLengths = ['monthly', 'yearly', 'premium-monthly', 'premium-yearly'];
    const planLength = supportedPlanLengths.includes(options.planLength)
      ? options.planLength
      : 'monthly';
    const valmarAvailable = await checkValmarAvailable();

    if (!valmarAvailable) {
      console.log('[GabPurchase] Valmar blocked for payment update, using Whop migration checkout');
      return openWhopPlusCheckout(
        { plusPlanId: planLength, mode: 'extend' },
        { fallbackFromValmar: true, purpose: 'update-payment' },
      );
    }

    pendingValmarUpdateRef.current = { updateUrl, options, planLength };
    setWhopCheckout(null);
    setCheckoutUrl(await accountApi.authorizeCheckoutUrl(updateUrl, 'update-payment'));
    setIsIframeLoaded(false);
    setIsOpen(true);

    return beginPendingCheckoutPromise(options);
  }, [beginPendingCheckoutPromise, openWhopPlusCheckout, userEmail, userId, userHeldForReview]);

  const contextValue = useMemo(() => ({
    openCheckout,
    closeCheckout,
    openCreditPacksCheckout,
    closeCreditPacksCheckout,
    openUpdatePaymentMethod,
    isOpen,
    isInternational,
    usesWhopCheckout: isInternational || isWhopSubscriber,
    iap, // Single IAP connection for subscriptions + credit packs (iOS/Android)
  }), [openCheckout, closeCheckout, openCreditPacksCheckout, closeCreditPacksCheckout, openUpdatePaymentMethod, isOpen, isInternational, isWhopSubscriber, iap]);

  return (
    <GabPurchaseContext.Provider value={contextValue}>
      {children}
      {/* Checkout modal - shows either Valmar iframe (US) or Whop embed (international) */}
      {Platform.OS === 'web' && isOpen && (
        <View style={{ position: 'fixed', zIndex: 9999, top: 0, right: 0, bottom: 0, left: 0, backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <Pressable onPress={() => closeCheckout('Backdrop close')} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, zIndex: 1 }} />

          {/* Close button on backdrop (outside iframe container) */}
          <Pressable
            onPress={() => closeCheckout('User closed with button')}
            accessibilityRole="button"
            accessibilityLabel="Close checkout"
            style={{ position: 'absolute', top: 12, right: 12, width: 36, height: 36, alignItems: 'center', justifyContent: 'center', zIndex: 3 }}
          >
            <Icon name="xmark" size={18} color='#ffffff' solid />
          </Pressable>

          {/* Scrollable container for Whop checkout */}
          {whopCheckout ? (
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingTop: isXS ? 50 : 40,
                paddingBottom: 40,
                paddingLeft: isXS ? 10 : 40,
                paddingRight: isXS ? 10 : 40,
              }}
            >
              {/* Whop embedded checkout with data attributes */}
              {whopCheckout.fallbackFromValmar && (
                <div
                  style={{
                    width: '100%',
                    maxWidth: 500,
                    marginBottom: 12,
                    padding: '10px 14px',
                    borderRadius: 8,
                    backgroundColor: colors.solid_color_block,
                    color: colors.text_color_secondary,
                    fontSize: 13,
                    lineHeight: '18px',
                  }}
                >
                  {whopCheckout.purpose === 'update-payment'
                    ? 'Your browser blocked our secure card form. Update your payment through alternate checkout below. Your remaining subscription time will be preserved.'
                    : 'Your browser blocked our secure card form. We switched you to alternate checkout so you can still complete payment.'}
                </div>
              )}
              {whopAutoOffer && !whopPaymentError && <View style={{ width: '100%', maxWidth: 500, marginBottom: 14 }}>
                <AutoTopupCheckoutOptIn pack={whopAutoOffer.pack} checked={whopAutoChecked} onChange={checked => {
                  setWhopAutoChecked(checked); whopAutoAuthorization.current = checked ? whopAutoOffer.settings : null;
                }} />
              </View>}
              {!!whopCheckout.promoHint && whopCheckout.purpose !== 'update-payment' && (
                <div
                  style={{
                    width: '100%',
                    maxWidth: 500,
                    marginBottom: 12,
                    padding: '10px 14px',
                    borderRadius: 8,
                    backgroundColor: colors.solid_color_block,
                    color: colors.text_color_primary,
                    fontSize: 14,
                    lineHeight: '20px',
                  }}
                >
                  {`Use code ${whopCheckout.promoHint} in the payment form below. It cannot be applied automatically.`}
                </div>
              )}
              {/* A failed attempt spends the session and leaves Whop's submit
                  button disabled, so the embed is replaced outright rather than
                  left on screen underneath an error. Hosted checkout is the
                  recovery path: it handles a fresh card like the embed does, and
                  runs the bank's 3D Secure challenge at the top level instead of
                  inside an iframe, which is what tends to stall. It has to be a
                  link the buyer clicks — opening a tab from Whop's async error
                  callback would be swallowed by popup blockers. */}
              {whopPaymentError ? (
                <div
                  style={{
                    width: '100%',
                    maxWidth: 500,
                    padding: '16px 18px',
                    borderRadius: 12,
                    backgroundColor: colors.solid_color_block,
                    color: colors.text_color_primary,
                    fontSize: 14,
                    lineHeight: '20px',
                  }}
                >
                  <div style={{ marginBottom: 8, fontWeight: '600' }}>{whopPaymentError}</div>
                  <div style={{ color: colors.text_color_secondary, fontSize: 13, lineHeight: '18px', marginBottom: 14 }}>
                    {whopCheckout.hostedCheckoutUrl
                      ? 'Continue on our secure checkout page, which opens in a new tab and can complete the verification your bank asks for.'
                      : 'You can start a new payment attempt below.'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {!!whopCheckout.hostedCheckoutUrl && (
                      <a
                        href={whopCheckout.hostedCheckoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '12px 16px',
                          borderRadius: 8,
                          background: '#3594d4',
                          color: '#ffffff',
                          fontSize: 15,
                          fontWeight: '600',
                          textAlign: 'center',
                          textDecoration: 'none',
                        }}
                      >
                        Open secure checkout
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={retryWhopCheckout}
                      style={{
                        padding: '10px 16px',
                        cursor: 'pointer',
                        background: 'transparent',
                        color: colors.text_color_secondary,
                        border: `1px solid ${colors.border_color_secondary}`,
                        borderRadius: 8,
                        fontSize: 14,
                      }}
                    >
                      {whopCheckout.hostedCheckoutUrl ? 'Use the form here instead' : 'Try again'}
                    </button>
                  </div>
                </div>
              ) : (
              <div
                ref={whopContainerRef}
                data-whop-checkout-plan-id={whopCheckout.planId}
                data-whop-checkout-session={whopCheckout.sessionId}
                data-whop-checkout-theme={isDarkTheme ? 'dark' : 'light'}
                data-whop-checkout-prefill-email={userEmail}
                data-whop-checkout-skip-redirect="true"
                data-whop-checkout-on-complete="__gabWhopCheckoutComplete"
                data-whop-checkout-on-payment-error="__gabWhopPaymentError"
                // Required for buyers who get bounced to their bank for 3D
                // Secure — without it the challenge has nowhere to return to.
                data-whop-checkout-return-url={whopReturnUrl}
                style={{ 
                  width: '100%', 
                  maxWidth: 500,
                  minHeight: 500,
                  backgroundColor: colors.solid_color_primary,
                  borderRadius: 12,
                }}
              />
              )}
            </div>
          ) : (
            <View
              style={{
                position: 'absolute',
                top: isXS ? 100 : 0,
                right: 0,
                bottom: 0,
                left: 0,
                alignItems: 'center',
                justifyContent: isXS ? 'flex-start' : 'center',
                paddingHorizontal: isXS ? 0 : 70,
                paddingVertical: isXS ? 0 : 70,
                zIndex: 2,
              }}
            >
              <View style={{ width: '100%', maxWidth: 945, height: '100%', position: 'relative', backgroundColor: colors.solid_color_primary, borderRadius: 12, overflow: 'hidden' }}>
                {/* Valmar iframe checkout for US users */}
                {!isIframeLoaded && (
                  <View style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.solid_color_primary }}>
                    <ActivityIndicator size="large" color={colors.text_color_primary} />
                  </View>
                )}

                {/* eslint-disable-next-line react/no-unknown-property */}
                <iframe
                  ref={checkoutIframeRef}
                  title="Gab Checkout"
                  src={checkoutUrl || ''}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                  onLoad={() => setIsIframeLoaded(true)}
                />
              </View>
            </View>
          )}
        </View>
      )}

    </GabPurchaseContext.Provider>
  );
}

export function useGabPurchase() {
  const context = useContext(GabPurchaseContext);
  if (context === null) {
    throw new Error('useGabPurchase must be used within a GabPurchaseProvider');
  }
  return context;
}
